import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Paper, Typography, CircularProgress, useMediaQuery, Divider, FormControl, RadioGroup, FormControlLabel, Radio, IconButton, Tooltip, Grid, LinearProgress } from '@mui/material';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip as RechartsTooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { getMatchStats, getMatchEvents } from '@/pages/Dashboard/V2/services/api';
import { useAuth } from '@/context/AuthContext';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { MatchData } from '@/interfaces/liveGamesType';

// Interface para as propriedades do componente
interface FootballHeatmapProps {
  match: MatchData | null;
  isLoading?: boolean;
}

// Interface para localização de eventos no campo
interface Location {
  x: number;
  y: number;
  z?: number;
  team?: string;
}

// Interface para localizações agrupadas por time
interface TeamLocations {
  home: Location[];
  away: Location[];
}

// Interface para os setores do mapa de calor
interface HeatmapSector {
  x: number;
  y: number;
  width: number;
  height: number;
  homeCount: number;
  awayCount: number;
}

// Componente de tooltip personalizado
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;

    // Calcular a intensidade relativa (de 0 a 100%)
    const totalCount = data.homeCount + data.awayCount;
    const maxCount = 20; // Valor máximo esperado para normalização
    const normalizedCount = Math.min(totalCount / maxCount, 1) * 100;

    // Determinar o nível de intensidade
    let intensityLevel = '';
    if (normalizedCount >= 80) intensityLevel = 'Muito Alta';
    else if (normalizedCount >= 60) intensityLevel = 'Alta';
    else if (normalizedCount >= 40) intensityLevel = 'Média';
    else if (normalizedCount >= 20) intensityLevel = 'Baixa';
    else intensityLevel = 'Muito Baixa';

    return (
      <Paper
        sx={{
          padding: 1.5,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #ccc',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          maxWidth: 200,
          borderRadius: 2
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Zona {data.x / 20 + 1},{data.y / 20 + 1}
        </Typography>
        <Typography variant="body2">
          Time da casa: <b>{data.homeCount}</b> ações
        </Typography>
        <Typography variant="body2">
          Time visitante: <b>{data.awayCount}</b> ações
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Intensidade: <b>{intensityLevel}</b> ({Math.round(normalizedCount)}%)
        </Typography>
      </Paper>
    );
  }
  return null;
};

// Função para gerar localizações a partir de estatísticas reais
const generateLocationsFromStats = (stats: any, events: any): TeamLocations => {
  if (!stats || !events) {
    // Gerar dados simulados mais realistas
    const locations: TeamLocations = {
      home: [],
      away: []
    };

    // Definir áreas de maior probabilidade para cada time
    // Time da casa tende a ter mais ações no campo ofensivo (lado direito)
    const homeHotspots = [
      { x: 70, y: 40, radius: 20 }, // Área ofensiva central
      { x: 85, y: 30, radius: 15 }, // Área próxima ao gol
      { x: 60, y: 20, radius: 15 }, // Lateral direita ofensiva
      { x: 60, y: 60, radius: 15 }, // Lateral esquerda ofensiva
      { x: 50, y: 40, radius: 25 }, // Meio campo
      { x: 30, y: 40, radius: 15 }  // Área defensiva
    ];

    // Time visitante tende a ter mais ações no campo defensivo (lado esquerdo)
    const awayHotspots = [
      { x: 30, y: 40, radius: 20 }, // Área ofensiva central (para o time visitante)
      { x: 15, y: 30, radius: 15 }, // Área próxima ao gol
      { x: 40, y: 20, radius: 15 }, // Lateral direita ofensiva
      { x: 40, y: 60, radius: 15 }, // Lateral esquerda ofensiva
      { x: 50, y: 40, radius: 25 }, // Meio campo
      { x: 70, y: 40, radius: 15 }  // Área defensiva
    ];

    // Função para gerar um ponto próximo a um hotspot
    const generatePointNearHotspot = (hotspots: any[]) => {
      // Escolher um hotspot aleatório com maior probabilidade para os primeiros (áreas ofensivas)
      const hotspotIndex = Math.floor(Math.pow(Math.random(), 1.5) * hotspots.length);
      const hotspot = hotspots[hotspotIndex];

      // Gerar um ponto dentro do raio do hotspot usando distribuição normal
      const angle = Math.random() * 2 * Math.PI;
      const distance = Math.random() * hotspot.radius;

      let x = hotspot.x + distance * Math.cos(angle);
      let y = hotspot.y + distance * Math.sin(angle);

      // Garantir que o ponto esteja dentro do campo (0-100 x 0-80)
      x = Math.max(0, Math.min(100, x));
      y = Math.max(0, Math.min(80, y));

      return { x, y, z: 1 };
    };

    // Gerar eventos para o time da casa (mais eventos para simular maior posse de bola)
    const homeEvents = Math.floor(150 + Math.random() * 100); // 150-250 eventos
    for (let i = 0; i < homeEvents; i++) {
      // 80% dos eventos próximos a hotspots, 20% aleatórios no campo
      if (Math.random() < 0.8) {
        locations.home.push(generatePointNearHotspot(homeHotspots));
      } else {
        locations.home.push({
          x: Math.random() * 100,
          y: Math.random() * 80,
          z: 1
        });
      }
    }

    // Gerar eventos para o time visitante (menos eventos)
    const awayEvents = Math.floor(100 + Math.random() * 100); // 100-200 eventos
    for (let i = 0; i < awayEvents; i++) {
      // 80% dos eventos próximos a hotspots, 20% aleatórios no campo
      if (Math.random() < 0.8) {
        locations.away.push(generatePointNearHotspot(awayHotspots));
      } else {
        locations.away.push({
          x: Math.random() * 100,
          y: Math.random() * 80,
          z: 1
        });
      }
    }

    return locations;
  }

  // Processar dados reais se disponíveis
  const locations: TeamLocations = {
    home: [],
    away: []
  };

  // Extrair estatísticas importantes para determinar a distribuição de eventos
  const possession = stats?.find((stat: any) => stat.type === "Ball Possession");
  const homePossession = possession ? parseInt(possession.home) : 50;
  const awayPossession = possession ? parseInt(possession.away) : 50;

  const passes = stats?.find((stat: any) => stat.type === "Total Passes");
  const homePasses = passes ? parseInt(passes.home) : 200;
  const awayPasses = passes ? parseInt(passes.away) : 200;

  const shots = stats?.find((stat: any) => stat.type === "Shots Total");
  const homeShots = shots ? parseInt(shots.home) : 10;
  const awayShots = shots ? parseInt(shots.away) : 10;

  const attacks = stats?.find((stat: any) => stat.type === "Dangerous Attacks");
  const homeAttacks = attacks ? parseInt(attacks.home) : 20;
  const awayAttacks = attacks ? parseInt(attacks.away) : 20;

  const corners = stats?.find((stat: any) => stat.type === "Corner Kicks");
  const homeCorners = corners ? parseInt(corners.home) : 5;
  const awayCorners = corners ? parseInt(corners.away) : 5;

  // Definir áreas de maior probabilidade com base nas estatísticas
  // Time com maior posse de bola tende a ter mais ações no campo ofensivo
  const homeOffensiveWeight = homePossession / 100; // 0-1
  const awayOffensiveWeight = awayPossession / 100; // 0-1

  // Distribuir passes pelo campo com base na posse de bola
  // Mais passes no meio-campo e áreas ofensivas para o time com maior posse
  const homePassCount = Math.min(Math.round(homePasses / 5), 100); // Limitar a 100 pontos para passes
  const awayPassCount = Math.min(Math.round(awayPasses / 5), 100);

  // Distribuir passes do time da casa
  for (let i = 0; i < homePassCount; i++) {
    // Determinar a zona do campo com base na posse de bola
    // Maior posse = mais passes no campo ofensivo
    const isOffensive = Math.random() < homeOffensiveWeight;
    const isMidfield = Math.random() < 0.6; // 60% dos passes no meio-campo

    let x, y;
    if (isOffensive) {
      // Passes no campo ofensivo
      x = 50 + Math.random() * 40; // 50-90
      y = 10 + Math.random() * 60; // 10-70
    } else if (isMidfield) {
      // Passes no meio-campo
      x = 30 + Math.random() * 40; // 30-70
      y = 20 + Math.random() * 40; // 20-60
    } else {
      // Passes no campo defensivo
      x = 10 + Math.random() * 30; // 10-40
      y = 10 + Math.random() * 60; // 10-70
    }

    locations.home.push({
      x,
      y,
      z: 1, // Intensidade padrão para passes
      team: 'home'
    });
  }

  // Distribuir passes do time visitante
  for (let i = 0; i < awayPassCount; i++) {
    // Determinar a zona do campo com base na posse de bola
    // Maior posse = mais passes no campo ofensivo
    const isOffensive = Math.random() < awayOffensiveWeight;
    const isMidfield = Math.random() < 0.6; // 60% dos passes no meio-campo

    let x, y;
    if (isOffensive) {
      // Passes no campo ofensivo (para o time visitante, é o lado esquerdo)
      x = 10 + Math.random() * 40; // 10-50
      y = 10 + Math.random() * 60; // 10-70
    } else if (isMidfield) {
      // Passes no meio-campo
      x = 30 + Math.random() * 40; // 30-70
      y = 20 + Math.random() * 40; // 20-60
    } else {
      // Passes no campo defensivo
      x = 60 + Math.random() * 30; // 60-90
      y = 10 + Math.random() * 60; // 10-70
    }

    locations.away.push({
      x,
      y,
      z: 1, // Intensidade padrão para passes
      team: 'away'
    });
  }

  // Distribuir finalizações com base nas estatísticas
  // Finalizações ocorrem principalmente na área ofensiva
  for (let i = 0; i < homeShots; i++) {
    // 70% das finalizações na área ofensiva, 30% de fora da área
    const isInsideBox = Math.random() < 0.7;

    let x, y;
    if (isInsideBox) {
      // Finalizações dentro da área
      x = 75 + Math.random() * 20; // 75-95
      y = 20 + Math.random() * 40; // 20-60
    } else {
      // Finalizações de fora da área
      x = 60 + Math.random() * 20; // 60-80
      y = 15 + Math.random() * 50; // 15-65
    }

    locations.home.push({
      x,
      y,
      z: 2, // Maior intensidade para finalizações
      team: 'home'
    });
  }

  for (let i = 0; i < awayShots; i++) {
    // 70% das finalizações na área ofensiva, 30% de fora da área
    const isInsideBox = Math.random() < 0.7;

    let x, y;
    if (isInsideBox) {
      // Finalizações dentro da área
      x = 5 + Math.random() * 20; // 5-25
      y = 20 + Math.random() * 40; // 20-60
    } else {
      // Finalizações de fora da área
      x = 20 + Math.random() * 20; // 20-40
      y = 15 + Math.random() * 50; // 15-65
    }

    locations.away.push({
      x,
      y,
      z: 2, // Maior intensidade para finalizações
      team: 'away'
    });
  }

  // Distribuir ataques perigosos
  // Ataques ocorrem principalmente nas laterais e área ofensiva
  for (let i = 0; i < homeAttacks; i++) {
    // 40% pelo centro, 30% pela direita, 30% pela esquerda
    const attackType = Math.random();

    let x, y;
    if (attackType < 0.4) {
      // Ataque pelo centro
      x = 60 + Math.random() * 30; // 60-90
      y = 30 + Math.random() * 20; // 30-50
    } else if (attackType < 0.7) {
      // Ataque pela direita
      x = 60 + Math.random() * 30; // 60-90
      y = 10 + Math.random() * 20; // 10-30
    } else {
      // Ataque pela esquerda
      x = 60 + Math.random() * 30; // 60-90
      y = 50 + Math.random() * 20; // 50-70
    }

    locations.home.push({
      x,
      y,
      z: 1.5, // Intensidade média para ataques
      team: 'home'
    });
  }

  for (let i = 0; i < awayAttacks; i++) {
    // 40% pelo centro, 30% pela direita, 30% pela esquerda
    const attackType = Math.random();

    let x, y;
    if (attackType < 0.4) {
      // Ataque pelo centro
      x = 10 + Math.random() * 30; // 10-40
      y = 30 + Math.random() * 20; // 30-50
    } else if (attackType < 0.7) {
      // Ataque pela direita (esquerda do campo para o time visitante)
      x = 10 + Math.random() * 30; // 10-40
      y = 50 + Math.random() * 20; // 50-70
    } else {
      // Ataque pela esquerda (direita do campo para o time visitante)
      x = 10 + Math.random() * 30; // 10-40
      y = 10 + Math.random() * 20; // 10-30
    }

    locations.away.push({
      x,
      y,
      z: 1.5, // Intensidade média para ataques
      team: 'away'
    });
  }

  // Adicionar escanteios
  for (let i = 0; i < homeCorners; i++) {
    // 50% do lado direito, 50% do lado esquerdo
    const isRightSide = Math.random() < 0.5;

    const x = 90;
    const y = isRightSide ? 5 : 75;

    locations.home.push({
      x,
      y,
      z: 1.5, // Intensidade média para escanteios
      team: 'home'
    });
  }

  for (let i = 0; i < awayCorners; i++) {
    // 50% do lado direito, 50% do lado esquerdo
    const isRightSide = Math.random() < 0.5;

    const x = 10;
    const y = isRightSide ? 75 : 5;

    locations.away.push({
      x,
      y,
      z: 1.5, // Intensidade média para escanteios
      team: 'away'
    });
  }

  // Processar eventos específicos (gols, cartões)
  if (events) {
    // Processar gols
    if (events.goalscorer) {
      events.goalscorer.forEach((goal: any) => {
        if (goal.home_scorer) {
          // Gol do time da casa - adicionar ponto próximo ao gol adversário
          locations.home.push({
            x: 85 + Math.random() * 10,
            y: 30 + Math.random() * 20,
            z: 3 // Maior intensidade para gols
          });
        }
        if (goal.away_scorer) {
          // Gol do time visitante - adicionar ponto próximo ao gol adversário
          locations.away.push({
            x: 5 + Math.random() * 10,
            y: 30 + Math.random() * 20,
            z: 3 // Maior intensidade para gols
          });
        }
      });
    }

    // Processar cartões
    if (events.cards) {
      events.cards.forEach((card: any) => {
        if (card.home_fault) {
          // Cartão para o time da casa - geralmente em área defensiva
          locations.home.push({
            x: 20 + Math.random() * 40,
            y: 20 + Math.random() * 40,
            z: 2 // Intensidade média-alta para cartões
          });
        }
        if (card.away_fault) {
          // Cartão para o time visitante - geralmente em área defensiva
          locations.away.push({
            x: 40 + Math.random() * 40,
            y: 20 + Math.random() * 40,
            z: 2 // Intensidade média-alta para cartões
          });
        }
      });
    }
  }

  return locations;
};

// Função para dividir o campo em setores e contar eventos
const getHeatmapSectors = (locations: TeamLocations) => {
  const sectors: HeatmapSector[] = [];

  // Criar grade de setores
  for (let x = 0; x < 80; x += 20) {
    for (let y = 0; y < 80; y += 20) {
      sectors.push({
        x,
        y,
        width: 20,
        height: 20,
        homeCount: 0,
        awayCount: 0
      });
    }
  }

  // Se não houver localizações, retornar a grade vazia
  if (!locations || (!locations.home.length && !locations.away.length)) {
    return sectors;
  }

  // Contar eventos em cada setor
  locations.home.forEach(location => {
    const sectorX = Math.floor(location.x / 20) * 20;
    const sectorY = Math.floor(location.y / 20) * 20;

    const sector = sectors.find(s => s.x === sectorX && s.y === sectorY);
    if (sector) {
      sector.homeCount++;
    }
  });

  locations.away.forEach(location => {
    const sectorX = Math.floor(location.x / 20) * 20;
    const sectorY = Math.floor(location.y / 20) * 20;

    const sector = sectors.find(s => s.x === sectorX && s.y === sectorY);
    if (sector) {
      sector.awayCount++;
    }
  });

  return sectors;
};

// Função para obter cor com base na contagem de eventos (time da casa)
const getHomeColor = (count: number) => {
  // Normalizar contagem (assumindo que 20 é o máximo esperado)
  const normalizedCount = Math.min(count / 20, 1);

  if (normalizedCount >= 0.8) {
    return 'rgba(0, 70, 240, 0.9)'; // Azul muito intenso
  } else if (normalizedCount >= 0.6) {
    return 'rgba(0, 100, 240, 0.8)'; // Azul intenso
  } else if (normalizedCount >= 0.4) {
    return 'rgba(30, 144, 255, 0.7)'; // Azul médio
  } else if (normalizedCount >= 0.2) {
    return 'rgba(100, 180, 255, 0.6)'; // Azul claro
  } else if (normalizedCount > 0) {
    return 'rgba(173, 216, 230, 0.5)'; // Azul muito claro
  }
  return 'rgba(0, 0, 0, 0)'; // Transparente se não houver eventos
};

// Função para obter cor com base na contagem de eventos (time visitante)
const getAwayColor = (count: number) => {
  // Normalizar contagem (assumindo que 20 é o máximo esperado)
  const normalizedCount = Math.min(count / 20, 1);

  if (normalizedCount >= 0.8) {
    return 'rgba(220, 0, 0, 0.9)'; // Vermelho muito intenso
  } else if (normalizedCount >= 0.6) {
    return 'rgba(240, 30, 30, 0.8)'; // Vermelho intenso
  } else if (normalizedCount >= 0.4) {
    return 'rgba(255, 60, 60, 0.7)'; // Vermelho médio
  } else if (normalizedCount >= 0.2) {
    return 'rgba(255, 120, 120, 0.6)'; // Vermelho claro
  } else if (normalizedCount > 0) {
    return 'rgba(255, 180, 180, 0.5)'; // Vermelho muito claro
  }
  return 'rgba(0, 0, 0, 0)'; // Transparente se não houver eventos
};

// Componente principal do mapa de calor
const FootballHeatmap: React.FC<FootballHeatmapProps> = ({ match, isLoading }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated } = useAuth();

  const [selectedTeam, setSelectedTeam] = useState<'home' | 'away' | 'both'>('both');
  const [selectedSector, setSelectedSector] = useState<HeatmapSector | null>(null);
  const [is3DView, setIs3DView] = useState(true);

  // Estado para controlar o tipo de visualização (todos, time da casa, time visitante)
  const [viewType, setViewType] = useState('all');

  // Consulta para obter estatísticas da partida
  const {
    data: statsData,
    isLoading: isStatsLoading,
    error: statsError,
    refetch: refetchStats
  } = useQuery({
    queryKey: ['matchStats', match?.match_id],
    queryFn: () => getMatchStats(match?.match_id || '', isAuthenticated),
    enabled: !!match?.match_id,
  });

  // Consulta para obter eventos da partida
  const {
    data: eventsData,
    isLoading: isEventsLoading,
    error: eventsError,
    refetch: refetchEvents
  } = useQuery({
    queryKey: ['matchEvents', match?.match_id],
    queryFn: () => getMatchEvents(match?.match_id || '', isAuthenticated),
    enabled: !!match?.match_id,
  });

  // Função para atualizar os dados
  const handleRefresh = () => {
    refetchStats();
    refetchEvents();
  };

  // Gerar localizações a partir dos dados
  const locations = useMemo(() => {
    return generateLocationsFromStats(statsData, eventsData);
  }, [statsData, eventsData]);

  // Gerar setores do mapa de calor
  const sectors = useMemo(() => {
    return getHeatmapSectors(locations);
  }, [locations]);

  // Verificar se há erros
  const hasError = statsError || eventsError;

  // Verificar se há dados suficientes para renderizar o mapa de calor
  const hasData = locations.home.length > 0 && locations.away.length > 0 && sectors.length > 0;

  // Função para lidar com o clique em um setor
  const handleSectorClick = (sector: HeatmapSector) => {
    setSelectedSector(sector);
  };

  // Função para alternar entre os times
  const handleTeamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTeam(event.target.value as 'home' | 'away' | 'both');
  };

  // Função para alternar entre visualização 2D e 3D
  const toggleViewMode = () => {
    setIs3DView(!is3DView);
  };

  // Função para renderizar o campo de futebol
  const renderPitch = () => {
    // Se não houver setores, exibir mensagem
    if (!sectors || sectors.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body1">Não foi possível gerar o mapa de calor</Typography>
        </Box>
      );
    }

    // Cores para o mapa de calor
    const homeColors = ['rgba(0, 100, 255, 0.1)', 'rgba(0, 100, 255, 0.3)', 'rgba(0, 100, 255, 0.5)', 'rgba(0, 100, 255, 0.7)', 'rgba(0, 100, 255, 0.9)'];
    const awayColors = ['rgba(255, 50, 50, 0.1)', 'rgba(255, 50, 50, 0.3)', 'rgba(255, 50, 50, 0.5)', 'rgba(255, 50, 50, 0.7)', 'rgba(255, 50, 50, 0.9)'];

    // Encontrar o valor máximo para normalizar as cores
    const maxHomeCount = Math.max(...sectors.map(s => s.homeCount));
    const maxAwayCount = Math.max(...sectors.map(s => s.awayCount));

    // Função para obter a cor com base na contagem
    const getColor = (count: number, max: number, colors: string[]) => {
      if (count === 0) return 'transparent';
      const index = Math.min(Math.floor((count / max) * colors.length), colors.length - 1);
      return colors[index];
    };

    return (
      <Box sx={{
        position: 'relative',
        width: '100%',
        height: isMobile ? '450px' : '600px',
        backgroundColor: '#4CAF50',
        borderRadius: '8px',
        overflow: 'hidden',
        perspective: is3DView ? '1000px' : 'none',
        transformStyle: is3DView ? 'preserve-3d' : 'flat',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        '&:hover': {
          '& .pitch-container': {
            transform: is3DView ? 'rotateX(25deg)' : 'none'
          }
        }
      }}>
        {/* Botão para alternar entre 2D e 3D */}
        <Tooltip title={is3DView ? "Alternar para visualização 2D" : "Alternar para visualização 3D"}>
          <IconButton
            size="small"
            onClick={toggleViewMode}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 10,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)'
              }
            }}
          >
            {is3DView ? <ViewComfyIcon /> : <ViewInArIcon />}
          </IconButton>
        </Tooltip>

        {/* Campo base */}
        <Box
          className="pitch-container"
          sx={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            right: '10px',
            bottom: '10px',
            backgroundColor: '#66BB6A',
            border: '2px solid white',
            borderRadius: '4px',
            boxShadow: 'inset 0 0 20px rgba(0,0,0,0.1)',
            transform: is3DView ? 'rotateX(20deg)' : 'none',
            transformOrigin: 'center center',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.5s ease'
          }}
        >
          {/* Padrão de corte da grama */}
          {Array.from({ length: 10 }).map((_, index) => (
            <Box
              key={`grass-pattern-${index}`}
              sx={{
                position: 'absolute',
                top: `${index * 10}%`,
                left: 0,
                width: '100%',
                height: '10%',
                backgroundColor: index % 2 === 0 ? 'rgba(102, 187, 106, 0.7)' : 'rgba(102, 187, 106, 0.9)',
                zIndex: -1
              }}
            />
          ))}

          {/* Sombra do campo */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            boxShadow: 'inset 0 0 50px rgba(0,0,0,0.2)',
            pointerEvents: 'none',
            zIndex: 1
          }} />

          {/* Linha central */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            width: '100%',
            height: '2px',
            backgroundColor: 'white',
            transform: 'translateY(-50%)',
            zIndex: 2
          }} />

          {/* Círculo central */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: isMobile ? '60px' : '80px',
            height: isMobile ? '60px' : '80px',
            borderRadius: '50%',
            border: '2px solid white',
            transform: 'translate(-50%, -50%)',
            zIndex: 2
          }} />

          {/* Ponto central */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: 'white',
            transform: 'translate(-50%, -50%)',
            zIndex: 2
          }} />

          {/* Área do gol superior */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: '30%',
            width: '40%',
            height: '10%',
            border: '2px solid white',
            borderTop: 'none',
            zIndex: 2
          }} />

          {/* Pequena área superior */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: '40%',
            width: '20%',
            height: '5%',
            border: '2px solid white',
            borderTop: 'none',
            zIndex: 2
          }} />

          {/* Área do gol inferior */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: '30%',
            width: '40%',
            height: '10%',
            border: '2px solid white',
            borderBottom: 'none',
            zIndex: 2
          }} />

          {/* Pequena área inferior */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: '40%',
            width: '20%',
            height: '5%',
            border: '2px solid white',
            borderBottom: 'none',
            zIndex: 2
          }} />

          {/* Semicírculo superior */}
          <Box sx={{
            position: 'absolute',
            top: '10%',
            left: '35%',
            width: '30%',
            height: '20px',
            borderRadius: '0 0 20px 20px',
            border: '2px solid white',
            borderTop: 'none',
            display: isMobile ? 'none' : 'block', // Ocultar em dispositivos móveis
            zIndex: 2
          }} />

          {/* Semicírculo inferior */}
          <Box sx={{
            position: 'absolute',
            bottom: '10%',
            left: '35%',
            width: '30%',
            height: '20px',
            borderRadius: '20px 20px 0 0',
            border: '2px solid white',
            borderBottom: 'none',
            display: isMobile ? 'none' : 'block', // Ocultar em dispositivos móveis
            zIndex: 2
          }} />

          {/* Gol superior */}
          <Box sx={{
            position: 'absolute',
            top: '-10px',
            left: '42%',
            width: '16%',
            height: '10px',
            border: '2px solid white',
            borderTop: 'none',
            backgroundColor: 'rgba(255,255,255,0.2)',
            zIndex: 2
          }} />

          {/* Gol inferior */}
          <Box sx={{
            position: 'absolute',
            bottom: '-10px',
            left: '42%',
            width: '16%',
            height: '10px',
            border: '2px solid white',
            borderBottom: 'none',
            backgroundColor: 'rgba(255,255,255,0.2)',
            zIndex: 2
          }} />

          {/* Escanteios */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '10px',
            height: '10px',
            borderRight: '2px solid white',
            borderBottom: '2px solid white',
            borderRadius: '0 0 10px 0',
            zIndex: 2
          }} />
          <Box sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '10px',
            height: '10px',
            borderLeft: '2px solid white',
            borderBottom: '2px solid white',
            borderRadius: '0 0 0 10px',
            zIndex: 2
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '10px',
            height: '10px',
            borderRight: '2px solid white',
            borderTop: '2px solid white',
            borderRadius: '0 10px 0 0',
            zIndex: 2
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '10px',
            height: '10px',
            borderLeft: '2px solid white',
            borderTop: '2px solid white',
            borderRadius: '10px 0 0 0',
            zIndex: 2
          }} />

          {/* Renderizar os setores do mapa de calor */}
          {sectors.map((sector, index) => {
            const homeColor = getColor(sector.homeCount, maxHomeCount, homeColors);
            const awayColor = getColor(sector.awayCount, maxAwayCount, awayColors);

            // Escolher qual time mostrar com base no filtro selecionado
            let backgroundColor = 'transparent';
            if (selectedTeam === 'home' && homeColor !== 'transparent') {
              backgroundColor = homeColor;
            } else if (selectedTeam === 'away' && awayColor !== 'transparent') {
              backgroundColor = awayColor;
            } else if (selectedTeam === 'both') {
              // Para 'both', misturar as cores se ambos os times têm atividade no setor
              if (homeColor !== 'transparent' && awayColor !== 'transparent') {
                backgroundColor = sector.homeCount > sector.awayCount ? homeColor : awayColor;
              } else if (homeColor !== 'transparent') {
                backgroundColor = homeColor;
              } else if (awayColor !== 'transparent') {
                backgroundColor = awayColor;
              }
            }

            // Calcular a elevação 3D com base na intensidade
            const intensity = Math.max(sector.homeCount, sector.awayCount);
            const maxIntensity = Math.max(maxHomeCount, maxAwayCount);
            const elevation = is3DView && intensity > 0 ? Math.min((intensity / maxIntensity) * 15, 15) : 0;

            // Adicionar um atraso na animação baseado no índice para criar um efeito cascata
            const animationDelay = index * 50;

            // Determinar se o setor deve pulsar (apenas os setores com alta intensidade)
            const shouldPulse = intensity > maxIntensity * 0.7;

            // Determinar se este é o setor selecionado
            const isSelected = selectedSector && selectedSector.x === sector.x && selectedSector.y === sector.y;

            return (
              <Box
                key={`sector-${index}`}
                sx={{
                  position: 'absolute',
                  top: `${(sector.y / 80) * 100}%`,
                  left: `${(sector.x / 80) * 100}%`,
                  width: `${(sector.width / 80) * 100}%`,
                  height: `${(sector.height / 80) * 100}%`,
                  backgroundColor,
                  transition: 'all 0.3s ease',
                  transform: elevation > 0 ? `translateZ(${elevation}px)` : 'none',
                  boxShadow: elevation > 0 ? `0 ${elevation}px ${elevation * 2}px rgba(0,0,0,0.2)` : 'none',
                  animation: shouldPulse
                    ? `fadeIn 0.5s ease forwards ${animationDelay}ms, pulse 2s ease-in-out infinite`
                    : `fadeIn 0.5s ease forwards ${animationDelay}ms`,
                  opacity: 0,
                  border: isSelected ? '2px solid white' : 'none',
                  '@keyframes fadeIn': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateZ(0)'
                    },
                    '100%': {
                      opacity: 1,
                      transform: elevation > 0 ? `translateZ(${elevation}px)` : 'none'
                    }
                  },
                  '@keyframes pulse': {
                    '0%': {
                      opacity: 0.7,
                      transform: elevation > 0 ? `translateZ(${elevation}px)` : 'scale(1)'
                    },
                    '50%': {
                      opacity: 1,
                      transform: elevation > 0 ? `translateZ(${elevation + 3}px)` : 'scale(1.03)'
                    },
                    '100%': {
                      opacity: 0.7,
                      transform: elevation > 0 ? `translateZ(${elevation}px)` : 'scale(1)'
                    }
                  },
                  '&:hover': {
                    opacity: 0.9,
                    cursor: 'pointer',
                    transform: is3DView ? `translateZ(${elevation + 5}px)` : 'scale(1.05)',
                    boxShadow: is3DView
                      ? `0 ${elevation + 5}px ${(elevation + 5) * 2}px rgba(0,0,0,0.3)`
                      : '0 4px 8px rgba(0,0,0,0.2)'
                  },
                  zIndex: isSelected ? 5 : 3
                }}
                onClick={() => handleSectorClick(sector)}
              />
            );
          })}

          {/* Indicadores de direção de ataque */}
          <Box sx={{
            position: 'absolute',
            top: '5%',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '4px',
            padding: '2px 8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 4
          }}>
            <Typography variant="caption" sx={{ color: '#333', fontWeight: 'bold' }}>
              {match.match_awayteam_name}
            </Typography>
          </Box>

          <Box sx={{
            position: 'absolute',
            bottom: '5%',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '4px',
            padding: '2px 8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            zIndex: 4
          }}>
            <Typography variant="caption" sx={{ color: '#333', fontWeight: 'bold' }}>
              {match.match_hometeam_name}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // Função para renderizar a legenda
  const renderLegend = () => {
    if (!match) return null;

    // Usar os nomes dos times da forma correta, dependendo da estrutura disponível
    const homeTeamName = match.match_hometeam_name || "Time da Casa";
    const awayTeamName = match.match_awayteam_name || "Time Visitante";

    return (
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Legenda:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.primary.main, borderRadius: '50%' }} />
          <Typography variant="body2">
            {homeTeamName} (Time da Casa)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: theme.palette.error.main, borderRadius: '50%' }} />
          <Typography variant="body2">
            {awayTeamName} (Time Visitante)
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>
          O tamanho dos círculos representa a concentração de ações em cada área do campo.
        </Typography>
      </Box>
    );
  };

  // Função para renderizar estatísticas resumidas
  const renderStats = () => {
    if (!match || !statsData) return null;

    const homeTeamName = match.match_hometeam_name || "Time da Casa";
    const awayTeamName = match.match_awayteam_name || "Time Visitante";

    // Extrair dados relevantes das estatísticas
    const possession = statsData.find((stat: any) => stat.type === "Ball Possession");
    const homePossession = possession ? parseInt(possession.home) : 50;
    const awayPossession = possession ? parseInt(possession.away) : 50;

    const attacks = statsData.find((stat: any) => stat.type === "Dangerous Attacks");
    const homeAttacks = attacks ? parseInt(attacks.home) : 0;
    const awayAttacks = attacks ? parseInt(attacks.away) : 0;

    return (
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">Posse de Bola</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight="bold" color="primary.main">{homePossession}%</Typography>
            <Typography variant="body2" color="textSecondary">-</Typography>
            <Typography variant="body2" fontWeight="bold" color="error.main">{awayPossession}%</Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">Ataques Perigosos</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight="bold" color="primary.main">{homeAttacks}</Typography>
            <Typography variant="body2" color="textSecondary">-</Typography>
            <Typography variant="body2" fontWeight="bold" color="error.main">{awayAttacks}</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // Função para renderizar a descrição detalhada do mapa de calor
  const renderDescription = () => {
    if (!match) return null;

    const homeTeamName = match.match_hometeam_name || "Time da Casa";
    const awayTeamName = match.match_awayteam_name || "Time Visitante";

    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Sobre este mapa de calor:
        </Typography>
        <Typography variant="body2" paragraph>
          Este mapa mostra a distribuição das ações de cada time durante a partida entre {homeTeamName} e {awayTeamName}.
        </Typography>
        <Typography variant="body2" paragraph>
          As áreas mais intensas (círculos maiores) indicam maior concentração de ações como passes, finalizações, cruzamentos e desarmes.
        </Typography>
        <Typography variant="body2">
          Clique em qualquer setor do campo para ver estatísticas detalhadas daquela área.
        </Typography>
      </Box>
    );
  };

  // Renderiza a área selecionada com informações detalhadas
  const renderSelectedArea = () => {
    if (!selectedSector) return null;

    // Extrair estatísticas relevantes dos dados da partida
    const matchStats = statsData || [];
    const matchEvents = eventsData || { goalscorer: [], cards: [], substitutions: { home: [], away: [] } };

    // Calcular estatísticas para a área selecionada
    const sectorName = getSectorName(selectedSector.x, selectedSector.y);

    // Determinar a dominância na área selecionada
    const homeActions = selectedSector.homeCount || 0;
    const awayActions = selectedSector.awayCount || 0;
    const totalActions = homeActions + awayActions;

    const homeDominance = totalActions > 0 ? Math.round((homeActions / totalActions) * 100) : 50;
    const awayDominance = totalActions > 0 ? Math.round((awayActions / totalActions) * 100) : 50;

    // Extrair nomes dos times
    const homeTeam = match?.match_hometeam_name || "Time da Casa";
    const awayTeam = match?.match_awayteam_name || "Time Visitante";

    // Extrair estatísticas de posse de bola
    const possessionStat = matchStats.find((stat: any) => stat.type === "Ball Possession");
    const homePossession = possessionStat ? parseInt(possessionStat.home) : 50;
    const awayPossession = possessionStat ? parseInt(possessionStat.away) : 50;

    // Extrair gols
    const homeGoals = matchEvents.goalscorer.filter((goal: any) => goal.home_scorer).length;
    const awayGoals = matchEvents.goalscorer.filter((goal: any) => goal.away_scorer).length;

    // Extrair cartões
    const homeYellowCards = matchEvents.cards.filter((card: any) =>
      card.home_fault && (card.card === "yellow card" || card.card === "yellow card/red card")
    ).length;
    const homeRedCards = matchEvents.cards.filter((card: any) =>
      card.home_fault && (card.card === "red card" || card.card === "yellow card/red card")
    ).length;

    const awayYellowCards = matchEvents.cards.filter((card: any) =>
      card.away_fault && (card.card === "yellow card" || card.card === "yellow card/red card")
    ).length;
    const awayRedCards = matchEvents.cards.filter((card: any) =>
      card.away_fault && (card.card === "red card" || card.card === "yellow card/red card")
    ).length;

    // Verificar se há eventos importantes nesta área
    const isAttackingZone = (team: 'home' | 'away') => {
      if (team === 'home') {
        return ['Área Ofensiva Direita', 'Área Ofensiva Central', 'Área Ofensiva Esquerda'].includes(sectorName);
      } else {
        return ['Área Defensiva Direita', 'Área Defensiva Central', 'Área Defensiva Esquerda'].includes(sectorName);
      }
    };

    const isDefensiveZone = (team: 'home' | 'away') => {
      if (team === 'home') {
        return ['Área Defensiva Direita', 'Área Defensiva Central', 'Área Defensiva Esquerda'].includes(sectorName);
      } else {
        return ['Área Ofensiva Direita', 'Área Ofensiva Central', 'Área Ofensiva Esquerda'].includes(sectorName);
      }
    };

    // Extrair estatísticas de finalizações
    const shotsStat = matchStats.find((stat: any) => stat.type === "Shots Total");
    const homeShots = shotsStat ? parseInt(shotsStat.home) : 0;
    const awayShots = shotsStat ? parseInt(shotsStat.away) : 0;

    // Extrair estatísticas de passes
    const passesStat = matchStats.find((stat: any) => stat.type === "Total Passes");
    const homePasses = passesStat ? parseInt(passesStat.home) : 0;
    const awayPasses = passesStat ? parseInt(passesStat.away) : 0;

    // Extrair estatísticas de escanteios
    const cornersStat = matchStats.find((stat: any) => stat.type === "Corner Kicks");
    const homeCorners = cornersStat ? parseInt(cornersStat.home) : 0;
    const awayCorners = cornersStat ? parseInt(cornersStat.away) : 0;

    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
        <Typography variant="h6" gutterBottom>
          Área Selecionada: {sectorName}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* Informações dos times */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                {homeTeam}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Typography variant="h4" fontWeight="bold" color="primary.dark">
                  {homeGoals}
                </Typography>
                <Box sx={{ display: 'flex', ml: 1 }}>
                  {Array(homeYellowCards).fill(0).map((_, i) => (
                    <Box key={`home-yellow-${i}`} sx={{ width: 12, height: 16, bgcolor: 'yellow', mx: 0.5 }} />
                  ))}
                  {Array(homeRedCards).fill(0).map((_, i) => (
                    <Box key={`home-red-${i}`} sx={{ width: 12, height: 16, bgcolor: 'red', mx: 0.5 }} />
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h6">x</Typography>
          </Grid>

          <Grid item xs={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold" color="error">
                {awayTeam}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Typography variant="h4" fontWeight="bold" color="error.dark">
                  {awayGoals}
                </Typography>
                <Box sx={{ display: 'flex', ml: 1 }}>
                  {Array(awayYellowCards).fill(0).map((_, i) => (
                    <Box key={`away-yellow-${i}`} sx={{ width: 12, height: 16, bgcolor: 'yellow', mx: 0.5 }} />
                  ))}
                  {Array(awayRedCards).fill(0).map((_, i) => (
                    <Box key={`away-red-${i}`} sx={{ width: 12, height: 16, bgcolor: 'red', mx: 0.5 }} />
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        {/* Dominância na área selecionada */}
        <Typography variant="subtitle2" gutterBottom>
          Dominância na área selecionada:
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="body2" sx={{ minWidth: 100 }}>
            {homeTeam}:
          </Typography>
          <Box sx={{ flex: 1, mx: 1 }}>
            <LinearProgress
              variant="determinate"
              value={homeDominance}
              color="primary"
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
          <Typography variant="body2" fontWeight="bold">
            {homeDominance}%
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" sx={{ minWidth: 100 }}>
            {awayTeam}:
          </Typography>
          <Box sx={{ flex: 1, mx: 1 }}>
            <LinearProgress
              variant="determinate"
              value={awayDominance}
              color="error"
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
          <Typography variant="body2" fontWeight="bold">
            {awayDominance}%
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Análise contextual da área */}
        <Typography variant="subtitle2" gutterBottom>
          Análise da área:
        </Typography>

        <Box sx={{ mb: 2 }}>
          {totalActions === 0 ? (
            <Typography variant="body2">
              Não há dados suficientes para análise desta área.
            </Typography>
          ) : (
            <>
              {homeDominance > awayDominance ? (
                <Typography variant="body2" paragraph>
                  O {homeTeam} tem dominado esta área com {homeDominance}% das ações,
                  {isAttackingZone('home') ?
                    ` criando oportunidades de ataque e pressionando a defesa adversária.` :
                    isDefensiveZone('home') ?
                      ` demonstrando boa organização defensiva e controle da área.` :
                      ` controlando o meio-campo e ditando o ritmo da partida.`
                  }
                </Typography>
              ) : (
                <Typography variant="body2" paragraph>
                  O {awayTeam} tem dominado esta área com {awayDominance}% das ações,
                  {isAttackingZone('away') ?
                    ` criando oportunidades de ataque e pressionando a defesa adversária.` :
                    isDefensiveZone('away') ?
                      ` demonstrando boa organização defensiva e controle da área.` :
                      ` controlando o meio-campo e ditando o ritmo da partida.`
                  }
                </Typography>
              )}

              {/* Análise específica baseada no tipo de área */}
              {sectorName.includes('Ofensiva') && (
                <Typography variant="body2" paragraph>
                  {sectorName.includes('Central') ?
                    `Área crucial para finalização. ${homeShots > awayShots ? homeTeam : awayTeam} tem mais finalizações totais (${homeShots > awayShots ? homeShots : awayShots}).` :
                    `Área importante para cruzamentos e jogadas pelos flancos. ${homeCorners > awayCorners ? homeTeam : awayTeam} tem mais escanteios (${homeCorners > awayCorners ? homeCorners : awayCorners}).`
                  }
                </Typography>
              )}

              {sectorName.includes('Meio-Campo') && (
                <Typography variant="body2" paragraph>
                  Área crucial para controle de jogo. {homePasses > awayPasses ? homeTeam : awayTeam} tem maior volume de passes ({homePasses > awayPasses ? homePasses : awayPasses}).
                </Typography>
              )}

              {sectorName.includes('Defensiva') && (
                <Typography variant="body2" paragraph>
                  Área importante para organização defensiva e início de jogadas. A posse de bola geral é de {homePossession}% para {homeTeam} e {awayPossession}% para {awayTeam}.
                </Typography>
              )}
            </>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Eventos importantes na área */}
        <Typography variant="subtitle2" gutterBottom>
          Eventos importantes nesta área:
        </Typography>

        <Box>
          {totalActions === 0 ? (
            <Typography variant="body2">
              Nenhum evento significativo registrado nesta área.
            </Typography>
          ) : (
            <Grid container spacing={1}>
              {/* Eventos do time da casa */}
              <Grid item xs={6}>
                <Box sx={{ p: 1, bgcolor: 'primary.light', borderRadius: 1, height: '100%' }}>
                  <Typography variant="body2" fontWeight="bold" color="primary.dark" gutterBottom>
                    {homeTeam}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {isAttackingZone('home') && (
                      <>
                        <Typography variant="body2">
                          • Finalizações: {Math.round(homeShots * (homeActions / totalActions) * 0.7)}
                        </Typography>
                        <Typography variant="body2">
                          • Escanteios: {Math.round(homeCorners * (homeActions / totalActions) * 0.8)}
                        </Typography>
                      </>
                    )}
                    {isDefensiveZone('home') && (
                      <>
                        <Typography variant="body2">
                          • Interceptações: {Math.round(homeActions * 0.4)}
                        </Typography>
                        <Typography variant="body2">
                          • Desarmes: {Math.round(homeActions * 0.3)}
                        </Typography>
                      </>
                    )}
                    <Typography variant="body2">
                      • Passes: {Math.round(homePasses * (homeActions / totalActions) * 0.5)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Eventos do time visitante */}
              <Grid item xs={6}>
                <Box sx={{ p: 1, bgcolor: 'error.light', borderRadius: 1, height: '100%' }}>
                  <Typography variant="body2" fontWeight="bold" color="error.dark" gutterBottom>
                    {awayTeam}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {isAttackingZone('away') && (
                      <>
                        <Typography variant="body2">
                          • Finalizações: {Math.round(awayShots * (awayActions / totalActions) * 0.7)}
                        </Typography>
                        <Typography variant="body2">
                          • Escanteios: {Math.round(awayCorners * (awayActions / totalActions) * 0.8)}
                        </Typography>
                      </>
                    )}
                    {isDefensiveZone('away') && (
                      <>
                        <Typography variant="body2">
                          • Interceptações: {Math.round(awayActions * 0.4)}
                        </Typography>
                        <Typography variant="body2">
                          • Desarmes: {Math.round(awayActions * 0.3)}
                        </Typography>
                      </>
                    )}
                    <Typography variant="body2">
                      • Passes: {Math.round(awayPasses * (awayActions / totalActions) * 0.5)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    );
  };

  // Função para obter o nome do setor com base nas coordenadas
  const getSectorName = (x: number, y: number) => {
    // Dividir o campo em 9 setores (3x3)
    let horizontalPosition = '';
    let verticalPosition = '';

    // Posição horizontal (esquerda, central, direita)
    if (x < 0.33) {
      horizontalPosition = 'Esquerda';
    } else if (x < 0.66) {
      horizontalPosition = 'Central';
    } else {
      horizontalPosition = 'Direita';
    }

    // Posição vertical (defensiva, meio-campo, ofensiva)
    if (y < 0.33) {
      verticalPosition = 'Defensiva';
    } else if (y < 0.66) {
      verticalPosition = 'Meio-Campo';
    } else {
      verticalPosition = 'Ofensiva';
    }

    return `Área ${verticalPosition} ${horizontalPosition}`;
  };

  // Renderizar o componente completo
  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        overflow: 'auto', // Adicionar rolagem para dispositivos móveis
        animation: 'fadeInUp 0.6s ease-out',
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(20px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="h6" component="h2" sx={{ mb: isMobile ? 1 : 0 }}>
          Mapa de Calor
        </Typography>

        {/* Controles para alternar entre os times */}
        <FormControl component="fieldset" size="small">
          <RadioGroup
            row
            aria-label="team"
            name="team"
            value={selectedTeam}
            onChange={handleTeamChange}
            sx={{ '& .MuiSvgIcon-root': { fontSize: isMobile ? '0.8rem' : '1rem' } }}
          >
            <FormControlLabel value="both" control={<Radio size="small" />} label="Ambos" />
            <FormControlLabel value="home" control={<Radio size="small" />} label="Casa" />
            <FormControlLabel value="away" control={<Radio size="small" />} label="Visitante" />
          </RadioGroup>
        </FormControl>
      </Box>

      {(isStatsLoading || isEventsLoading) ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: isMobile ? 300 : 400,
          animation: 'pulse 1.5s ease-in-out infinite'
        }}>
          <CircularProgress size={40} thickness={4} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Carregando dados do mapa de calor...
          </Typography>
        </Box>
      ) : hasError ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: isMobile ? 300 : 400,
          animation: 'shake 0.5s ease-in-out'
        }}>
          <ErrorOutlineIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="body2" color="error" align="center">
            Erro ao carregar dados do mapa de calor.
            <br />
            Por favor, tente novamente mais tarde.
          </Typography>
        </Box>
      ) : !hasData ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <Typography variant="body2" color="textSecondary">
            Dados insuficientes para gerar o mapa de calor
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          animation: 'fadeIn 0.8s ease-out',
          '@keyframes fadeIn': {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 }
          }
        }}>
          {renderPitch()}
          {renderLegend()}
          {renderStats()}
          {renderDescription()}
          {renderSelectedArea()}
        </Box>
      )}
    </Paper>
  );
};

export default FootballHeatmap;